import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthPage } from '../../hooks/useAuthPage';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { CheckCircle2 } from 'lucide-react';

interface PricingDetails {
  name: string;
  price: number;
  trial: number;
}

const pricingMap: Record<string, PricingDetails> = {
  basic: { name: 'Basic', price: 9, trial: 14 },
  pro: { name: 'Pro', price: 29, trial: 14 },
  enterprise: { name: 'Enterprise', price: 99, trial: 30 },
};

interface SignupData {
  email?: string;
  selectedPlan?: string;
}

/** After a tinycms signup. (TinyCMS signups end in their own flow.) */
export default function OrderConfirmationPage() {
  const location = useLocation();
  const signupData: SignupData = (location.state as SignupData) || {};
  return <LegacyConfirmation signupData={signupData} />;
}

function LegacyConfirmation({ signupData }: { signupData: SignupData }) {
  useAuthPage(); // Enable natural page scrolling
  const navigate = useNavigate();

  // Redirect if no signup data
  if (!signupData.email || !signupData.selectedPlan) {
    navigate('/signup');
    return null;
  }

  const planDetails = pricingMap[signupData.selectedPlan] || pricingMap.basic;
  const trialEndDate = new Date();
  trialEndDate.setDate(trialEndDate.getDate() + planDetails.trial);

  const handleGoToDashboard = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <Card className="max-w-2xl w-full border-0 bg-core-dark">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <CardTitle>Welcome Aboard! 🎉</CardTitle>
          <CardDescription>Your account has been successfully created</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-muted p-6 rounded-lg space-y-4">
            <h3 className="font-semibold">Account Details</h3>
            <div className="grid gap-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Email</span>
                <span className="font-medium">{signupData.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Plan</span>
                <span className="font-medium">{planDetails.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Monthly Price</span>
                <span className="font-medium">${planDetails.price}/month</span>
              </div>
            </div>
          </div>

          {signupData.selectedPlan === 'basic' && (
            <div className="bg-cmp-surface border border-gray-200 p-4 rounded-lg">
              <h4 className="font-semibold mb-2 text-black">Your Free Trial</h4>
              <p className="text-black">
                You have <span className="font-semibold text-black">{planDetails.trial} days</span>{' '}
                to try out all features for free. Your card will be charged ${planDetails.price} on{' '}
                <span className="font-semibold text-foreground">
                  {trialEndDate.toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
                .
              </p>
            </div>
          )}

          <div className="space-y-2">
            <h4 className="font-semibold">What's Next?</h4>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                <span>Check your email for a confirmation message</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                <span>Complete your profile setup</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                <span>Start exploring all the features</span>
              </li>
              {signupData.selectedPlan === 'basic' && (
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  <span>Cancel anytime during your trial with no charges</span>
                </li>
              )}
            </ul>
          </div>
        </CardContent>
        <CardFooter>
          <Button onClick={handleGoToDashboard} className="w-full">
            Get Started
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
